import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Home, MapPin, User, Phone, Mail, PawPrint, Car, Info, DoorOpen, Lock, Megaphone, Wind, ExternalLink, Copy, Key, Save, X, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

// Simple stable id generator for new access-item rows.
const newAccessId = () => `ai_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export function PropertyCard({
  prop, units, codesByUnit = {}, linksByUnit = {},
  expanded, toggles, handlers,
}) {
  const { details: isDetailsOpen, units: isUnitsOpen } = expanded;
  const [expandedUnit, setExpandedUnit] = useState(null);

  // Sort units numerically within property
  const sortedUnits = [...units].sort((a, b) => {
    const aNum = parseInt(a.unit_number, 10);
    const bNum = parseInt(b.unit_number, 10);
    if (isNaN(aNum) && isNaN(bNum)) return (a.unit_number || '').localeCompare(b.unit_number || '');
    if (isNaN(aNum)) return 1;
    if (isNaN(bNum)) return -1;
    return aNum - bNum;
  });

  return (
    <Card className="overflow-hidden border-l-4 border-l-primary/30 shadow-sm" data-testid="properties-table-row">
      <div className="p-5 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="font-heading text-lg font-semibold tracking-tight truncate">{prop.name}</h2>
              {prop.building_id != null && (
                <Badge variant="outline" className="text-xs flex-shrink-0 tabular-nums">Bldg #{prop.building_id}</Badge>
              )}
              <Badge variant="secondary" className="text-xs flex-shrink-0 tabular-nums">
                {units.length} {units.length === 1 ? 'Unit' : 'Units'}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{prop.address}</span>
            </div>
            {units.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {sortedUnits.map(u => (
                  <Badge key={u.id} variant="outline" className="text-xs font-normal gap-1">
                    <Home className="h-3 w-3" />
                    {u.unit_number}
                    <span className="text-muted-foreground">({u.unit_size === 'other' ? u.unit_size_custom : u.unit_size})</span>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handlers.editProp} data-testid="property-edit-button">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={handlers.deleteProp} data-testid="property-delete-button">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex gap-2 mt-4 pt-3 border-t border-border/50">
          <Button variant={isDetailsOpen ? "secondary" : "ghost"} size="sm" className="h-8 text-xs gap-1.5" onClick={toggles.details} data-testid="property-details-toggle">
            {isDetailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Info className="h-3.5 w-3.5" /> Property Details
          </Button>
          <Button variant={isUnitsOpen ? "secondary" : "ghost"} size="sm" className="h-8 text-xs gap-1.5" onClick={toggles.units} data-testid="property-units-toggle">
            {isUnitsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Home className="h-3.5 w-3.5" /> Units ({units.length})
          </Button>
        </div>
      </div>

      {isDetailsOpen && (
        <div className="px-5 pb-5 border-t bg-amber-50/40">
          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><User className="h-3.5 w-3.5" /> Owner / Manager</div>
              <p className="text-sm font-medium">{prop.owner_manager_name || '-'}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Phone className="h-3.5 w-3.5" /> Phone</div>
              <p className="text-sm">{prop.owner_manager_phone || '-'}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Mail className="h-3.5 w-3.5" /> Email</div>
              <p className="text-sm">{prop.owner_manager_email || '-'}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Car className="h-3.5 w-3.5" /> Parking</div>
              <p className="text-sm">{prop.available_parking || 'Not specified'}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><PawPrint className="h-3.5 w-3.5" /> Pets</div>
              <div className="flex items-center gap-2">
                <Badge variant={prop.pets_permitted ? 'default' : 'secondary'} className="text-xs">
                  {prop.pets_permitted ? 'Permitted' : 'Not Permitted'}
                </Badge>
                {prop.pets_permitted && prop.pet_notes && (
                  <span className="text-xs text-muted-foreground">{prop.pet_notes}</span>
                )}
              </div>
            </div>
            {prop.building_amenities && prop.building_amenities.length > 0 && (
              <div className="space-y-1 md:col-span-2 lg:col-span-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amenities</p>
                <div className="flex flex-wrap gap-1.5">
                  {prop.building_amenities.map((a) => (
                    <Badge key={a} variant="outline" className="text-xs font-normal">{a}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          {prop.additional_notes && (
            <div className="mt-4 pt-3 border-t border-border/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Notes</p>
              <p className="text-sm text-muted-foreground">{prop.additional_notes}</p>
            </div>
          )}

          {/* Property-level access items (backup keys / codes). Not tied
              to any single unit — think building-wide access. */}
          <PropertyAccessSection
            items={prop.access_items || []}
            onSave={handlers.saveAccessItems}
          />
        </div>
      )}

      {isUnitsOpen && (
        <div className="border-t">
          <div className="px-5 py-3 flex items-center justify-between bg-muted/30">
            <h3 className="text-sm font-semibold">Units</h3>
            <Button size="sm" className="h-7 text-xs" onClick={handlers.createUnit} data-testid="units-create-button">
              <Plus className="h-3 w-3 mr-1" /> Add Unit
            </Button>
          </div>
          {units.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <Home className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No units added yet</p>
              <Button size="sm" variant="outline" className="mt-3 text-xs" onClick={handlers.createUnit}>Add First Unit</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/20">
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Unit #</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Size</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Base Rent</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Add'l Costs</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Available From</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide">Close Date</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wide w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedUnits.map((u, idx) => {
                    const addlTotal = (u.additional_monthly_costs || []).reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
                    const codes = codesByUnit[u.id];
                    const mlinks = linksByUnit[u.id];
                    const hasFilter = u.filter_size || u.filter_last_changed;
                    // Row is always expandable now — always show chevron + edit buttons inside.
                    const hasInfo = true;
                    const isExpanded = expandedUnit === u.id;
                    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50';
                    return (
                      <UnitRowGroup
                        key={u.id}
                        unit={u}
                        addlTotal={addlTotal}
                        codes={codes}
                        mlinks={mlinks}
                        hasFilter={hasFilter}
                        hasInfo={hasInfo}
                        isExpanded={isExpanded}
                        rowBg={rowBg}
                        onToggle={() => setExpandedUnit(isExpanded ? null : u.id)}
                        onEdit={() => handlers.editUnit(u)}
                        onDelete={() => handlers.deleteUnit(u.id)}
                        onEditCodes={() => handlers.editDoorCodes?.(u)}
                        onEditLinks={() => handlers.editMarketing?.(u)}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function UnitRowGroup({ unit, addlTotal, codes, mlinks, hasFilter, hasInfo, isExpanded, rowBg, onToggle, onEdit, onDelete, onEditCodes, onEditLinks }) {
  return (
    <>
      <TableRow
        className={`${rowBg} hover:bg-muted/40 cursor-pointer`}
        data-testid="units-table-row"
        onClick={onToggle}
      >
        <TableCell className="font-medium">
          <div className="flex items-center gap-1.5">
            {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            {unit.unit_number}
            {codes && <DoorOpen className="h-3 w-3 text-muted-foreground" />}
            {mlinks && <Megaphone className="h-3 w-3 text-muted-foreground" />}
            {hasFilter && <Wind className="h-3 w-3 text-muted-foreground" />}
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="secondary" className="text-xs">{unit.unit_size === 'other' ? unit.unit_size_custom : unit.unit_size}</Badge>
        </TableCell>
        <TableCell className="tabular-nums">${parseFloat(unit.base_rent).toLocaleString()}</TableCell>
        <TableCell className="tabular-nums">
          {addlTotal > 0 ? (
            <span className="text-xs">
              +${addlTotal.toLocaleString()}
              <span className="text-muted-foreground ml-1">({(unit.additional_monthly_costs || []).map(c => c.name).join(', ')})</span>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell className="text-sm">{unit.availability_start_date}</TableCell>
        <TableCell className="text-sm">{unit.close_date || '-'}</TableCell>
        <TableCell>
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit} data-testid="unit-edit-button">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={onDelete} data-testid="unit-delete-button">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow className="bg-amber-50/40">
          <TableCell colSpan={7} className="p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Door Codes */}
              <div data-testid="unit-door-codes-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                    <DoorOpen className="h-3.5 w-3.5" />Door Codes
                  </p>
                  {onEditCodes && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 gap-1" onClick={onEditCodes} data-testid="unit-edit-door-codes-btn">
                      <Pencil className="h-3 w-3" />{codes ? 'Edit' : 'Add'}
                    </Button>
                  )}
                </div>
                {codes ? (
                  <>
                    <div className="grid grid-cols-5 gap-1.5">
                      <div className="text-xs"><span className="text-muted-foreground block">Admin</span><span className="font-mono flex items-center gap-0.5"><Lock className="h-3 w-3" />****</span></div>
                      <div className="text-xs"><span className="text-muted-foreground block">Housekeeping</span><span className="font-mono">{codes.housekeeping_code || '-'}</span></div>
                      <div className="text-xs">
                        <span className="text-muted-foreground block">Guest</span>
                        <span className="font-mono font-bold text-sm inline-flex items-center gap-1">
                          {codes.guest_code || '-'}
                          {codes.guest_code && <CopyGuestCodeButton code={codes.guest_code} />}
                        </span>
                      </div>
                      <div className="text-xs"><span className="text-muted-foreground block">Backup 1</span><span className="font-mono">{codes.backup_code_1 || '-'}</span></div>
                      <div className="text-xs"><span className="text-muted-foreground block">Backup 2</span><span className="font-mono">{codes.backup_code_2 || '-'}</span></div>
                    </div>
                    {codes.custom_codes && codes.custom_codes.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-dashed border-border/60 grid grid-cols-5 gap-1.5" data-testid="unit-custom-codes-preview">
                        {codes.custom_codes.map((c) => (
                          <div key={c.id || c.label} className="text-xs">
                            <span className="text-muted-foreground block truncate" title={c.note || ''}>{c.label || 'Untitled'}</span>
                            <span className="font-mono">{c.value || '-'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No codes set</p>
                )}
              </div>
              {/* Marketing Links */}
              <div data-testid="unit-marketing-links-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                    <Megaphone className="h-3.5 w-3.5" />Marketing Links
                  </p>
                  {onEditLinks && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 gap-1" onClick={onEditLinks} data-testid="unit-edit-marketing-btn">
                      <Pencil className="h-3 w-3" />{mlinks ? 'Edit' : 'Add'}
                    </Button>
                  )}
                </div>
                {mlinks ? (
                  <div className="flex flex-wrap gap-1.5">
                    {mlinks.airbnb_link && <UnitLinkBadge label="Airbnb" url={mlinks.airbnb_link} />}
                    {mlinks.furnished_finder_link && <UnitLinkBadge label="Furnished Finder" url={mlinks.furnished_finder_link} />}
                    {mlinks.photos_link && <UnitLinkBadge label="Photos" url={mlinks.photos_link} />}
                    {mlinks.additional_links?.map((al) => <UnitLinkBadge key={`${al.name}-${al.url}`} label={al.name} url={al.url} />)}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No links set</p>
                )}
              </div>
              {/* AC Filter */}
              <div data-testid="unit-ac-filter-section">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
                  <Wind className="h-3.5 w-3.5" />AC Filter
                </p>
                {hasFilter ? (
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div><span className="text-muted-foreground block">Size</span><span className="font-medium">{unit.filter_size || '—'}</span></div>
                    <div><span className="text-muted-foreground block">Last Changed</span><span className="font-medium">{unit.filter_last_changed || '—'}</span></div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No filter info</p>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function UnitLinkBadge({ label, url }) {
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };
  return (
    <span className="inline-flex items-center gap-1 border rounded-full px-2 py-0.5 bg-card text-xs">
      <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
        <ExternalLink className="h-3 w-3" />{label}
      </a>
      <button className="hover:text-foreground text-muted-foreground" onClick={copy} aria-label="Copy link"><Copy className="h-3 w-3" /></button>
    </span>
  );
}

function CopyGuestCodeButton({ code }) {
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code);
    toast.success('Guest code copied');
  };
  return (
    <button
      className="text-muted-foreground hover:text-foreground transition-colors"
      onClick={copy}
      aria-label="Copy guest code"
      title="Copy guest code"
      data-testid="copy-guest-code-btn"
    >
      <Copy className="h-3 w-3" />
    </button>
  );
}

/**
 * PropertyAccessSection
 *
 * Building-wide backup keys and access codes attached to the property
 * itself (not to any single unit). Each entry has:
 *   - kind: "key" or "code"        (determines the icon + label of the value input)
 *   - label: short name             ("Front Gate", "Storage Room 3", "Mailbox Key")
 *   - value: the actual code/tag    (only rendered as a plain string — codes not masked)
 *   - location: where the key lives ("Lobby safe #2", "Owner's box")
 *   - notes: free-form
 *
 * The section persists via a single `onSave(newItems)` callback that
 * echoes back the entire updated array — matches the pattern used by
 * DoorCodeEditDialog.
 */
function PropertyAccessSection({ items, onSave }) {
  const [draft, setDraft] = useState(null); // null | { id, kind, label, value, location, notes, isNew }
  const [saving, setSaving] = useState(false);

  const startAdd = (kind) => setDraft({
    id: newAccessId(), kind, label: '', value: '', location: '', notes: '', isNew: true,
  });
  const startEdit = (item) => setDraft({ ...item, isNew: false });
  const cancel = () => setDraft(null);

  const commit = async () => {
    if (!draft) return;
    if (!draft.label.trim()) { toast.error('Label is required'); return; }
    if (!draft.value.trim()) { toast.error(draft.kind === 'key' ? 'Key label / tag is required' : 'Code is required'); return; }
    const clean = {
      id: draft.id,
      kind: draft.kind,
      label: draft.label.trim(),
      value: draft.value.trim(),
      location: draft.location.trim(),
      notes: draft.notes.trim(),
    };
    const next = draft.isNew
      ? [...(items || []), clean]
      : (items || []).map((it) => (it.id === draft.id ? clean : it));
    setSaving(true);
    try { await onSave?.(next); setDraft(null); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this key/code from the property?')) return;
    const next = (items || []).filter((it) => it.id !== id);
    await onSave?.(next);
  };

  return (
    <div className="mt-4 pt-3 border-t border-border/50" data-testid="property-access-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <Key className="h-3.5 w-3.5" />
          Property Access — Backup Keys &amp; Codes
          {items?.length > 0 && (
            <span className="text-[10px] text-muted-foreground/70 font-normal">({items.length})</span>
          )}
        </p>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => startAdd('key')} data-testid="property-access-add-key">
            <Plus className="h-3 w-3" /> Add key
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => startAdd('code')} data-testid="property-access-add-code">
            <Plus className="h-3 w-3" /> Add code
          </Button>
        </div>
      </div>

      {(!items || items.length === 0) && !draft && (
        <p className="text-xs text-muted-foreground italic">
          Track building-wide access items — spare key locations, gate codes, storage keys — visible to any admin viewing this property.
        </p>
      )}

      {items && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {items.map((it) => (
            <AccessItemRow key={it.id} item={it} onEdit={() => startEdit(it)} onDelete={() => remove(it.id)} />
          ))}
        </div>
      )}

      {draft && (
        <AccessItemEditor draft={draft} setDraft={setDraft} onCommit={commit} onCancel={cancel} saving={saving} />
      )}
    </div>
  );
}

function AccessItemRow({ item, onEdit, onDelete }) {
  const Icon = item.kind === 'key' ? Key : KeyRound;
  const kindLabel = item.kind === 'key' ? 'Key' : 'Code';
  return (
    <div className="rounded-md border bg-card p-2.5 hover:border-primary/40 transition-colors" data-testid="property-access-item">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 h-6 w-6 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
          <Icon className="h-3.5 w-3.5 text-emerald-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium truncate">{item.label}</span>
            <Badge variant="secondary" className="text-[9px] px-1 py-0">{kindLabel}</Badge>
          </div>
          <p className="mt-0.5 font-mono text-xs text-foreground break-all">{item.value}</p>
          {item.location && (
            <p className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" />{item.location}</p>
          )}
          {item.notes && (
            <p className="mt-0.5 text-[11px] text-muted-foreground italic break-words">{item.notes}</p>
          )}
        </div>
        <div className="flex gap-0.5 flex-shrink-0">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onEdit} data-testid="property-access-edit-btn">
            <Pencil className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={onDelete} data-testid="property-access-delete-btn">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function AccessItemEditor({ draft, setDraft, onCommit, onCancel, saving }) {
  const isKey = draft.kind === 'key';
  return (
    <div className="mt-3 rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="property-access-editor">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-primary">
          {draft.isNew ? `Add ${isKey ? 'key' : 'code'}` : `Edit ${isKey ? 'key' : 'code'}`}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[11px]">Label *</Label>
          <Input
            value={draft.label}
            onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
            placeholder={isKey ? 'e.g. Front door spare' : 'e.g. Main gate'}
            className="h-8 text-sm"
            data-testid="property-access-label"
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">{isKey ? 'Key tag / identifier *' : 'Code *'}</Label>
          <Input
            value={draft.value}
            onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
            placeholder={isKey ? 'e.g. Red tag #A12' : 'e.g. 4820#'}
            className={`h-8 text-sm ${isKey ? '' : 'font-mono'}`}
            data-testid="property-access-value"
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-[11px]">Location</Label>
          <Input
            value={draft.location}
            onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
            placeholder={isKey ? 'Where is this key stored? e.g. Lobby safe #2' : 'Where does this code apply? e.g. Roof access panel'}
            className="h-8 text-sm"
            data-testid="property-access-location"
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-[11px]">Notes</Label>
          <Textarea
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            rows={2}
            placeholder="Optional context — expiration, who has copies, etc."
            className="text-sm"
            data-testid="property-access-notes"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={onCancel}>
          <X className="h-3 w-3" /> Cancel
        </Button>
        <Button size="sm" className="h-7 text-xs gap-1" onClick={onCommit} disabled={saving} data-testid="property-access-save">
          <Save className="h-3 w-3" /> {saving ? 'Saving…' : (draft.isNew ? 'Add' : 'Update')}
        </Button>
      </div>
    </div>
  );
}
